import { HttpErrorResponse } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { ModalController, Platform } from '@ionic/angular/standalone';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { combineLatest, EMPTY, from, Observable, of, timer } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { CreateIntentResponse, PaymentService } from 'src/app/services/payment.service';
import { SlipService } from 'src/app/services/slip.service';
import { Outcome, ParentHubStripe } from 'src/plugins/parent-hub-stripe';
import * as fromStore from '../../../../store';
import * as slipPaymentIntentActions from '../../../../store/actions/slip-payment-intent.action';
import { EditPaymentAmountModalComponent } from '../../../edit-payment-amount-modal/edit-payment-amount-modal.component';
import { SlipComponentInfo } from '../../student-slip-item-choices-modal/student-slip-item-choices-modal.store';
import { ItemChoicesComponentInfo, PaymentStatus } from '../../student-slip-modal.store';

export interface OperationState<T> {
  loading: boolean;
  result: T;
}

export interface PaymentMethod {
  methodId: string;
  cardBrand: string;
  cardLast4: string;
}

class ProgressError extends Error {
  constructor(
    message: string,
    public canRetry: boolean
  ) {
    super(message);
  }
}

export interface FormState {
  paymentStatus: PaymentStatus;
  slipComponentInfo: SlipComponentInfo;
  itemChoicesComponentInfo: ItemChoicesComponentInfo;
  payNowAmount: number;
  canPayWithGooglePay: boolean;
  canPayWithApplePay: boolean;
  paymentMethod: PaymentMethod;
  platformButtonClass: string;
  intentState: OperationState<CreateIntentResponse>;
  customerLoadState: OperationState<void>;
  progressError: ProgressError;
  canEditAmount: boolean;
  reservationExpiresOn: string;
  refreshReservationEmitter: EventEmitter<void>;
}

@Injectable()
export class StudentSlipItemChoicesPaymentHybridStore extends ComponentStore<FormState> {
  constructor(
    private store: Store,
    private slipService: SlipService,
    private paymentService: PaymentService,
    private modalCtrl: ModalController,
    private platform: Platform
  ) {
    super({
      paymentStatus: null,
      slipComponentInfo: null,
      itemChoicesComponentInfo: null,
      payNowAmount: null,
      canPayWithGooglePay: false,
      canPayWithApplePay: false,
      paymentMethod: null,
      platformButtonClass: null,
      intentState: null,
      customerLoadState: null,
      progressError: null,
      canEditAmount: false,
      reservationExpiresOn: null,
      refreshReservationEmitter: null
    });
  }

  readonly setItemChoicesComponentInfo = this.updater((state, value: ItemChoicesComponentInfo) => ({
    ...state,
    itemChoicesComponentInfo: value,
    // first time though intialise payNowAmount
    payNowAmount: state.payNowAmount ?? value.balance,
    canEditAmount: value.minimumAmount > 0 && value.balance >= value.minimumAmount + 100
  }));

  readonly vm$ = this.select(state => state).pipe(tap(vm => console.log('Hybrid form vm$ ', vm)));
  readonly paymentStatus$ = this.select(state => state.paymentStatus).pipe(
    tap(paymentStatus => console.log('Hybrid form paymentStatus$ ', paymentStatus))
  );

  private createIntent(): Observable<CreateIntentResponse> {
    this.patchState({ intentState: { loading: true, result: null } });
    return this.slipService
      .createSlipItemChoicesIntent(
        this.get().slipComponentInfo.schoolId,
        this.get().slipComponentInfo.studentId,
        this.get().slipComponentInfo.slipId,
        this.get().slipComponentInfo.componentId,
        this.get().payNowAmount
      )
      .pipe(
        tap((result) => {
          this.patchState({ intentState: { loading: false, result: result } });
        }),
        catchError((error) => {
          throw getIntentError(error);
        })
      );
  }

  // Fetches customer information from our api and sets up the default payment method
  // (if saved)
  readonly initialiseCustomer = this.effect((trigger$) => {
    this.patchState({ customerLoadState: { loading: true, result: null } });
    return trigger$.pipe(
      switchMap(() => {
        // Each school can be in live or test mode with stripe
        const stripeTestMode = this.get().itemChoicesComponentInfo.paymentKey.startsWith('pk_test');
        return combineLatest([
          timer(1000),
          this.paymentService.getEphemeralKey(stripeTestMode).pipe(
            switchMap(async (result) => {
              await ParentHubStripe.initialiseCustomerSession({
                ephemeralKey: result.ephemeralKey
              });

              const findPaymentResult = await ParentHubStripe.findPaymentMethod({
                paymentMethodId: this.get().itemChoicesComponentInfo.paymentMethodId
              });
              return findPaymentResult;
            }),
            // error can come from our api request or one of the stripe calls failing
            // catch and return so we don't short circuit our timer when error occurs
            catchError((error: HttpErrorResponse | string) => {
              const message =
                (error as HttpErrorResponse)?.status === 0
                  ? `It looks like you're offline. Please check your connection and try again.`
                  : `Something went wrong, please try again later`;
              return of(message);
            })
          )
        ]).pipe(
          tapResponse(
            ([_, result]) => {
              this.patchState({ customerLoadState: { loading: false, result: null } });
              if (typeof result === 'string') {
                this.patchState({ progressError: new ProgressError(result, false) });
              } else {
                // result will be undefined if no paymentMethodId has been stored
                if (result) {
                  this.patchState({
                    paymentMethod: {
                      methodId: this.get().itemChoicesComponentInfo.paymentMethodId,
                      cardBrand: result?.cardBrand,
                      cardLast4: result?.cardLast4
                    }
                  });
                }
              }
            },
            () => {
              this.patchState({
                customerLoadState: { loading: false, result: null },
                progressError: new ProgressError(`Something went wrong, please try again later`, false)
              });
            }
          )
        );
      })
    );
  });

  // Simulate payNow flow...
  // readonly payNow = this.effect((trigger$) => {
  //   return trigger$.pipe(
  //     tap(() => {
  //       this.setPaymentStatus({ inProgress: true, method: 'card', intentId: null });
  //       this.setIntentState({ loading: true, result: null });
  //     }),
  //     delay(5000),
  //     tap(() => {
  //       this.setIntentState({
  //         loading: false,
  //         result: { amount: 100, ephemeralKey: '123', intentId: 123, intentSecretKey: 'aaa' }
  //       });
  //     }),
  //     delay(5000),
  //     tap(() => {
  //       this.setPaymentStatus({ inProgress: false, method: 'card', intentId: 123 });
  //     })
  //   );
  // });

  // Pay using the non platform payment button
  readonly payNow = this.effect((trigger$) => {
    return trigger$.pipe(
      switchMap(() => {
        const expired = this.get().reservationExpiresOn && moment().isAfter(moment(this.get().reservationExpiresOn));
        if (expired) {
          this.get().refreshReservationEmitter.emit();
          return EMPTY;
        }
        this.patchState({ paymentStatus: { inProgress: true, method: 'card', intentId: null }, progressError: null });
        return combineLatest([
          timer(1000),
          this.createIntent().pipe(
            switchMap(intent =>
              from(
                ParentHubStripe.confirmPayment({
                  publishableKey: this.get().itemChoicesComponentInfo.paymentKey,
                  intentSecretKey: intent.intentSecretKey,
                  paymentMethodId: this.get().paymentMethod.methodId
                })
              ).pipe(
                map((confirmPaymentResult) => {
                  if (confirmPaymentResult.outcome === Outcome.Succeeded) {
                    return {
                      intent: intent
                    };
                  } else {
                    throw new ProgressError(confirmPaymentResult.code, true);
                  }
                }),
                catchError((error) => {
                  throw new ProgressError(error.message, true);
                })
              )
            )
          )
        ]).pipe(
          tapResponse(
            ([_, result]) => {
              this.patchState({
                paymentStatus: { inProgress: false, method: 'card', intentId: result.intent.intentId }
              });
              this.store.dispatch(
                slipPaymentIntentActions.addIntent({
                  intent: {
                    id: result.intent.intentId,
                    version: 2,
                    tenant: this.get().slipComponentInfo.schoolId,
                    studentId: this.get().slipComponentInfo.studentId,
                    slipId: this.get().slipComponentInfo.slipId,
                    method: 'card',
                    nextCheckTimestamp: 0,
                    attempt: 0
                  }
                })
              );
            },
            (error: ProgressError) => {
              this.patchState({
                paymentStatus: null,
                progressError: error
              });
            }
          )
        );
      })
    );
  });

  // Note: Google pay differs from Apple Pay in that the intent is confirmed outside the
  // payment sheet
  readonly payWithGooglePay = this.effect((trigger$) => {
    return trigger$.pipe(
      switchMap(() => {
        const expired = this.get().reservationExpiresOn && moment().isAfter(moment(this.get().reservationExpiresOn));
        if (expired) {
          this.get().refreshReservationEmitter.emit();
          return EMPTY;
        }
        this.patchState({ progressError: null });

        const action = from(
          ParentHubStripe.payWithGooglePay({
            publishableKey: this.get().itemChoicesComponentInfo.paymentKey,
            intentSecretKey: null,
            totalPrice: this.get().payNowAmount,
            currencyCode: 'GBP'
          })
        );

        return action.pipe(
          switchMap((result: any) => {
            // Platform payment can return without having selected a payment method
            // e.g. on emulator when google account not set up so throw error with
            // no message to exit flow silently
            if (!result) throw new ProgressError('', true);
            this.patchState({
              paymentStatus: {
                inProgress: true,
                method: 'google',
                intentId: null
              }
            });
            return this.createIntent().pipe(
              switchMap(intent =>
                from(
                  ParentHubStripe.confirmPayment({
                    publishableKey: this.get().itemChoicesComponentInfo.paymentKey,
                    intentSecretKey: intent.intentSecretKey,
                    paymentMethodId: result.paymentMethodId
                  })
                ).pipe(
                  map((confirmPaymentResult) => {
                    if (confirmPaymentResult.outcome === Outcome.Succeeded) {
                      return {
                        intent: intent
                      };
                    } else {
                      throw new ProgressError(confirmPaymentResult.code, true);
                    }
                  }),
                  catchError((error) => {
                    throw new ProgressError(error.message, true);
                  })
                )
              )
            );
          }),
          tapResponse(
            (result) => {
              this.patchState({
                paymentStatus: {
                  inProgress: false,
                  method: 'google',
                  intentId: result.intent.intentId
                }
              });
              this.store.dispatch(
                slipPaymentIntentActions.addIntent({
                  intent: {
                    id: result.intent.intentId,
                    version: 2,
                    tenant: this.get().slipComponentInfo.schoolId,
                    studentId: this.get().slipComponentInfo.studentId,
                    slipId: this.get().slipComponentInfo.slipId,
                    method: 'google',
                    nextCheckTimestamp: 0,
                    attempt: 0
                  }
                })
              );
            },
            (error: ProgressError) => {
              this.patchState({
                paymentStatus: null,
                progressError: error
              });
            }
          )
        );
      })
    );
  });

  // Note: Apple pay differs from Google Pay in that the intent is confirmed during the
  // payment sheet flow. Hence we need to be notified when the intent is required and pass it
  // to the plugin to continue with
  readonly payWithApplePay = this.effect((trigger$) => {
    return trigger$.pipe(
      switchMap(() => {
        const expired = this.get().reservationExpiresOn && moment().isAfter(moment(this.get().reservationExpiresOn));
        if (expired) {
          this.get().refreshReservationEmitter.emit();
          return EMPTY;
        }
        this.patchState({ progressError: null });

        const action = from(
          ParentHubStripe.payWithApplePay({
            publishableKey: this.get().itemChoicesComponentInfo.paymentKey,
            // Intent key will be asked for by the plugin and supplied via continueApplePayWithIntent
            intentSecretKey: '',
            totalPrice: this.get().payNowAmount,
            currencyCode: 'GBP',
            schoolName: this.get().slipComponentInfo.schoolName
          })
        );

        return action.pipe(
          tapResponse(
            (result) => {
              // User dismissed payment sheet?
              if (!result) return;

              this.patchState({
                paymentStatus: {
                  inProgress: false,
                  method: 'apple',
                  intentId: this.get().intentState.result.intentId
                }
              });
              this.store.dispatch(
                slipPaymentIntentActions.addIntent({
                  intent: {
                    id: this.get().intentState.result.intentId,
                    version: 2,
                    tenant: this.get().slipComponentInfo.schoolId,
                    studentId: this.get().slipComponentInfo.studentId,
                    slipId: this.get().slipComponentInfo.slipId,
                    method: 'apple',
                    nextCheckTimestamp: 0,
                    attempt: 0
                  }
                })
              );
            },
            (error: { message: string }) => {
              // Becaue the apple pay process is two step we will already have set the ProgressError
              // if the intent fails so don't overwrite it with a general error from the plugin call
              if (error.message !== 'intent-error') {
                this.patchState({ paymentStatus: null, progressError: new ProgressError(error.message, true) });
              }
            }
          )
        );
      })
    );
  });

  // Fetches the payment intent for Apple Pay continuation
  readonly loadApplePayIntent = this.effect((trigger$) => {
    return trigger$.pipe(
      switchMap(() => {
        return this.createIntent().pipe(
          tapResponse(
            (result) => {
              ParentHubStripe.continueApplePayWithIntent({
                intentSecretKey: result.intentSecretKey
              });
            },
            (error: ProgressError) => {
              ParentHubStripe.continueApplePayWithIntent({
                intentSecretKey: null
              });
              this.patchState({ paymentStatus: null, progressError: error });
            }
          )
        );
      })
    );
  });

  readonly editAmount = this.effect((trigger$) => {
    return trigger$.pipe(
      tap(async () => {
        const state = this.get();
        const operation = (amount: number): Observable<CreateIntentResponse> => {
          // Create dummy response since we now fetch intent prior to starting payment action
          return of({
            intentId: null,
            intentSecretKey: null,
            ephemeralKey: null,
            amount: amount
          });
        };
        const modal = await this.modalCtrl.create({
          component: EditPaymentAmountModalComponent,
          componentProps: {
            paymentTitle: state.slipComponentInfo.title,
            balance: state.itemChoicesComponentInfo.balance,
            minimumAmount: state.itemChoicesComponentInfo.minimumAmount,
            intentRequest: operation
          },
          backdropDismiss: false
        });
        await modal.present();
        const { data } = await modal.onWillDismiss();
        if (data) {
          const intentResponse = data.intentResponse as CreateIntentResponse;
          this.patchState({ payNowAmount: intentResponse.amount });
        }
      })
    );
  });

  readonly updatePaymentMethod = this.effect((params$: Observable<{ methodId: string }>) => {
    return params$.pipe(
      tap((params: { methodId: string }) => {
        this.store.dispatch(new fromStore.UpdatePaymentMethodId({ paymentMethodId: params.methodId }));
      })
    );
  });

  readonly initialiseStripe = this.effect((trigger$) => {
    return trigger$.pipe(
      tap(async () => {
        if (this.platform.is('android')) {
          const initGooglePayResult = await ParentHubStripe.initialiseGooglePay();
          this.patchState({
            canPayWithGooglePay: initGooglePayResult.canPayByGooglePay,
            platformButtonClass: 'android'
          });
        }

        if (this.platform.is('ios')) {
          const initApplePayResult = await ParentHubStripe.initialiseApplePay();
          this.patchState({
            canPayWithApplePay: initApplePayResult.canPayByApplePay,
            platformButtonClass: 'ios'
          });
        }

        await ParentHubStripe.stripeInit({
          publishableKey: this.get().itemChoicesComponentInfo.paymentKey
        });

        this.initialiseCustomer();
      })
    );
  });

  readonly chooseMethod = this.effect((trigger$) => {
    return trigger$.pipe(
      tap(() => {
        ParentHubStripe.choosePaymentMethod({
          paymentMethodId: this.get().itemChoicesComponentInfo.paymentMethodId
        }).catch((error) => {
          console.log(error);
        });
      })
    );
  });
}
function getIntentError(httpError: HttpErrorResponse): ProgressError {
  const intentErrors: { [code: number]: { message: string; canRetry: boolean } } = {
    2000: {
      message: 'This slip has been closed by the school. Making payment online is no longer available.',
      canRetry: false
    },
    2001: {
      message: 'This slip is no longer active for your child. Please contact the school for more information.',
      canRetry: false
    },
    2003: { message: 'Payment not possible. Please contact the school for more information.', canRetry: false },
    2004: {
      message: `Your payment couldn't be processed because the balance on the slip has already been paid in full.`,
      canRetry: false
    }
  };
  let errorResult;
  if (httpError.status === 0)
    errorResult = {
      message: `It looks like you're offline. Please check your connection and try again.`,
      canRetry: true
    };
  else if (httpError.status === 404)
    errorResult = {
      message: `We were unable to process your payment. Please contact support@parenthub.co.uk.`,
      canRetry: true
    };
  else if (httpError?.error?.ErrorCode) {
    const error = intentErrors[httpError.error.ErrorCode];
    errorResult = error ?? {
      message:
        `Payment not possible. Please contact support@parenthub.co.uk quoting error code: ` + httpError.error.ErrorCode,
      canRetry: true
    };
  } else
    errorResult = { message: `We weren't able to process your payment this time. Please try again.`, canRetry: true };
  return new ProgressError(errorResult.message, errorResult.canRetry);
}
